/* eslint-disable react-hooks/exhaustive-deps */
import {memo, useContext, useMemo} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import FilterWrapperBasic, {
  filterWrapperBasicAllSelectionsId as allSelectionsId,
  filterWrapperBasicTypes
} from '@adv-mt/mt-filter-wrapper-basic'
import SelectRange, {FIELD_NAME} from '@adv-mt/mt-form-select-range'
import IconEuro from '@adv-mt/mt-svg-icons/lib/Euro.js'

import {LEGACY_WRAPPER} from './config.js'

const EMPTY_STRING = ''

const DEFAULT_ACTIVE_TAB = 2

const FormPrice = ({
  initialSearch = {},
  isBasicContent = false,
  isInline,
  onChange = () => {},
  overlayContentRef,
  pageName = '',
  showLabel = false,
  showPriceTypeLabel = false,
  type,
  value = {},
  withLabel = true,
  wrapper = LEGACY_WRAPPER
}) => {
  const {domain, i18n} = useContext(Context)
  const {categoryId, offerTypeId, hasInstalment, instalment, entry, maxTerms} = initialSearch

  const config = domain.get('config')

  const {
    financingEntry,
    financingTerms,
    instalmentAmounts,
    prices,
    priceChipSeeds,
    priceToChipSeeds,
    DEFAULT_PRICE,
    FILTER_NAMES,
    ADS_FILTERS_MAP
  } = config.get('search')

  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const {DEFAULT_FILTERS_SEARCH} = config.get('searchFilters')

  const getFilterValue = ({hasInstalment, value}) => {
    if (hasInstalment)
      return {
        // get backwards compatibility with old instalment values until we remove the old wrapper
        from: wrapper === LEGACY_WRAPPER ? instalment?.from || DEFAULT_PRICE : instalment.from,
        to: wrapper === LEGACY_WRAPPER ? instalment?.to || DEFAULT_PRICE : instalment.to
      }
    return {
      // get backwards compatibility with old instalment values until we remove the old wrapper
      from: wrapper === LEGACY_WRAPPER ? value?.from || DEFAULT_PRICE : value.from,
      to: wrapper === LEGACY_WRAPPER ? value?.to || DEFAULT_PRICE : value.to
    }
  }

  const isInstalmentFilterEnabled = useMemo(
    () => ADS_FILTERS_MAP[categoryId]?.[offerTypeId]?.some(filter => filter === FILTER_NAMES.INSTALMENT),
    [categoryId, offerTypeId]
  )

  const isLabelVisible = showLabel || !isInstalmentFilterEnabled

  const currentValue = getFilterValue({hasInstalment, value})

  const localizePriceValue = priceValue => ({
    id: priceValue,
    label: i18n.c(priceValue)
  })

  const filterOptions = hasInstalment ? instalmentAmounts : prices
  const options = filterOptions.map(localizePriceValue)

  const getInstalementHelpText = ({entry, maxTerms}) => {
    if (entry !== null && maxTerms === null) return i18n.t('SEARCH_BOX_SELECT_PRICE_INSTALMENT_WITH_ENTRY_TEXT')
    if (entry === null && maxTerms !== null) return i18n.t('SEARCH_BOX_SELECT_PRICE_INSTALMENT_WITH_TERMS_TEXT')
    if (entry !== null && maxTerms !== null) return ''
    return i18n.t('SEARCH_BOX_SELECT_PRICE_INSTALMENT_TEXT')
  }

  const getHelpText = ({hasInstalment, entry, maxTerms}) => {
    if (hasInstalment) return getInstalementHelpText({entry, maxTerms})
    return i18n.t('SEARCH_BOX_SELECT_PRICE_CASH_TEXT')
  }

  const helpText = getHelpText({hasInstalment, entry, maxTerms})

  const selectOptions = {
    from: {
      name: FIELD_NAME.FROM,
      options
    },
    to: {
      name: FIELD_NAME.TO,
      options
    }
  }

  const getLabel = () => {
    if (showPriceTypeLabel) return hasInstalment ? 'COMMON.FEE_MONTHLY' : 'COMMON.PRICE'
    return 'COMMON.PRICE'
  }

  const labelAccessibility = i18n.t(hasInstalment ? 'COMMON.FEE_MONTHLY' : 'COMMON.PRICE')

  const label = isLabelVisible ? i18n.t(getLabel()) : EMPTY_STRING
  const fieldNameValue = hasInstalment ? FILTER_NAMES.INSTALMENT : FILTER_NAMES.PRICE

  const showFiltersEntryAndTerms = hasInstalment && !isBasicContent
  const showHelpText = showLabel && !hasInstalment

  const financingEntryOptions = useMemo(
    () =>
      financingEntry?.map(item => {
        return {
          id: item,
          label: i18n.c(item)
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const financingTermsOptions = useMemo(
    () =>
      financingTerms?.map(item => {
        return {
          id: item,
          label: i18n.t('FINANCING_INFO.TERMS_LABEL', {terms: item, years: item / 12})
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleChangeFinancingEntryTerms = ({name, value}) => {
    const [entryTermsValue] = value

    onChange(null, {
      field: name,
      lastFilter: TRACKING_FILTER_NAMES[name],
      value: entryTermsValue === allSelectionsId ? DEFAULT_FILTERS_SEARCH[name] : Number(entryTermsValue)
    })
  }

  const handleResetFilter = ({filters}) => {
    const [filterName] = filters
    onChange(null, {
      field: filterName,
      value: DEFAULT_FILTERS_SEARCH[filterName]
    })
  }

  return (
    <div className="mt-FormPrice">
      <SelectRange
        fromChipSeeds={!hasInstalment ? priceChipSeeds : undefined}
        toChipSeedsBase={!hasInstalment ? priceToChipSeeds : undefined}
        defaultActiveTabIndex={DEFAULT_ACTIVE_TAB}
        fieldName={fieldNameValue}
        formatterLabel={({value}) => i18n.c(value)}
        helpText={showHelpText && helpText}
        initialSearch={initialSearch}
        isBasicContent={isBasicContent}
        isInline={isInline}
        label={label}
        labelAccessibility={labelAccessibility}
        onChange={onChange}
        options={selectOptions}
        overlayContentRef={overlayContentRef}
        pageName={pageName}
        suffix={!hasInstalment ? '€' : undefined}
        suffixIcon={!hasInstalment ? <IconEuro size="medium" color="currentColor" /> : undefined}
        type={type}
        value={currentValue}
        withLabel={withLabel}
        wrapper={wrapper}
      />
      {showFiltersEntryAndTerms && (
        <FilterWrapperBasic
          filters={[
            {
              allSelectionsLabel: i18n.t('FINANCING_INFO.ENTRANCE_FEE'),
              label: i18n.t('FINANCING_INFO.ENTRANCE_FEE'),
              name: FILTER_NAMES.FINANCING_ENTRY,
              options: financingEntryOptions,
              placeholder: entry
                ? `${i18n.t('FINANCING_INFO.ENTRANCE_FEE')} ${i18n.c(entry)}`
                : i18n.t('FINANCING_INFO.ENTRANCE_FEE'),
              type
            },
            {
              allSelectionsLabel: i18n.t('FINANCING_INFO.TERMS_TO'),
              label: i18n.t('FINANCING_INFO.TERMS_TO'),
              name: FILTER_NAMES.FINANCING_TERMS,
              options: financingTermsOptions,
              placeholder: maxTerms
                ? i18n.t('FINANCING_INFO.TERMS_LABEL', {terms: maxTerms, years: maxTerms / 12})
                : i18n.t('FINANCING_INFO.TERMS_TO'),
              type
            }
          ]}
          group="financingEntryTermsGroup"
          helpText={helpText}
          legend={i18n.t('FILTERS_GROUPS.FINANCING_ENTRY_TERMS_GROUP.LEGEND.DEFAULT')}
          onChange={handleChangeFinancingEntryTerms}
          onReset={handleResetFilter}
          overlayContentRef={overlayContentRef}
          value={{
            [FILTER_NAMES.FINANCING_ENTRY]: entry ? [entry] : [allSelectionsId],
            [FILTER_NAMES.FINANCING_TERMS]: maxTerms ? [maxTerms] : [allSelectionsId]
          }}
          wrapper={
            // Tech-debt: Remove this condition when we remove the old wrapper https://jira.ets.mpi-internal.com/browse/MTR-91807
            wrapper === 'modalPopoversHorizontalAutoClose'
              ? filterWrapperBasicTypes.MODAL_POPOVERS_AUTOCLOSE
              : filterWrapperBasicTypes.RIGHT_POPOVERS_AUTOCLOSE
          }
        />
      )}
    </div>
  )
}

const FormPriceBasicContent = props => <FormPrice {...props} isBasicContent />
FormPriceBasicContent.displayName = 'FormPriceBasicContent'

FormPrice.displayName = 'FormPrice'
FormPrice.propTypes = {
  initialSearch: PropTypes.object,
  isBasicContent: PropTypes.bool,
  isInline: PropTypes.bool,
  onChange: PropTypes.func,
  pageName: PropTypes.string,
  overlayContentRef: PropTypes.node,
  showLabel: PropTypes.bool,
  showPriceTypeLabel: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.object,
  withLabel: PropTypes.bool,
  wrapper: PropTypes.string
}

const MemoFormPrice = memo(FormPrice)
MemoFormPrice.displayName = 'FormPrice'

const MemoFormPriceBasicContent = memo(FormPriceBasicContent)
MemoFormPriceBasicContent.displayName = 'FormPriceBasicContent'

export {MemoFormPriceBasicContent as FormPriceBasicContent}

export default MemoFormPrice
